// services/supplierDailyOrders.service.js
//
// Advisory write actions for the Suppliers-Club Daily Orders page (design spec
// 2026-07-05). The supplier ADVISES, the collector decides — these writes are
// annotations that never change item status, money, or the collector's outcome
// (locked brief L4). Per line item: reserve («معايا»), mark-out-of-stock
// («نواقص»), or clear the signal back to unanswered.
//
// Uses jsonApiClient ONLY so the Sanctum SPA cookie + X-XSRF-TOKEN + shared
// 401/419 interceptors flow through. Do NOT introduce a new axios instance —
// see the comment block in src/httpConfig/config/axios.js (multiple session
// cookies → "400 Request Header Too Large").
//
// Error normalization mirrors src/services/orderBoxItem.service.js, split per the
// delivered status-code contract (delivered brief + P2-A brief's table):
//   - 409 = the write gate closed underneath you (item left PENDING / plan
//     closed) → SUPPLIER_WRITE_FROZEN, so the composable reverts + silently
//     refetches + toasts the (client-derived) lock reason.
//   - 422 = bad payload (e.g. reservedQty out of range) → a PLAIN validation
//     error, NOT frozen. Reporting a genuine bad-request bug as "someone already
//     started this item" would be a lie in front of the supplier; the composable
//     surfaces it as a generic retry toast instead.
import { jsonApiClient } from '@/httpConfig/config/axios.js';

// ── Endpoint templates — the delivered BE slice registers these as FLAT custom
//    actions on the `supplier-box-items` resource (id = plan-item id), with NO
//    boxId segment and NO `-actions/` prefix (routes/jsonapi.php uses the
//    prefix-less `->actions(...)` form for this resource — verified against the
//    registration). The read base ('/supplier-boxes') lives in
//    useSupplierDailyOrders.js. ────────────────────────────────────────────────
const ITEM_BASE = (itemId) => `/supplier-box-items/${itemId}`;
const RESERVE_PATH = (itemId) => `${ITEM_BASE(itemId)}/reserve`;
const OOS_PATH = (itemId) => `${ITEM_BASE(itemId)}/out-of-stock`;
const SIGNAL_PATH = (itemId) => `${ITEM_BASE(itemId)}/signal`;

/**
 * Tag attached to errors the server raised because the write-gate is closed
 * (the collector already started this item, or the box/day is closed). The
 * composable reverts the optimistic signal, silently refetches so the now-frozen
 * truth re-renders, and toasts the plain-language reason. ONLY 409 → this; a 422
 * is a bad-payload validation error, never a "frozen" state.
 */
export const SUPPLIER_WRITE_FROZEN = 'supplier-write-frozen';

function extractJsonApiDetail(error) {
  return error?.response?.data?.errors?.[0]?.detail || null;
}

// Best-effort extraction of a machine frozenReason from a 409 gate rejection.
// The delivered slice does NOT ship a `frozenReason` field (the FE derives it
// client-side from the box read — see deriveFrozenReason in SupplierSignal.enum),
// so this stays null in practice and the composable falls back to its derived /
// default reason after the reconcile refetch. Kept as a forward-compatible probe
// of the likely JSON:API spots in case BE adds one later; harmless when absent.
function extractFrozenReason(error) {
  const err0 = error?.response?.data?.errors?.[0];
  return (
    err0?.meta?.frozenReason ??
    err0?.meta?.frozen_reason ??
    error?.response?.data?.meta?.frozenReason ??
    error?.response?.data?.meta?.frozen_reason ??
    null
  );
}

function normalizeError(error) {
  const status = error?.response?.status;
  const detail = extractJsonApiDetail(error);
  const normalized = new Error(detail || error?.message || 'Request failed');
  normalized.status = status;
  normalized.detail = detail;
  normalized.original = error;

  // 409 ONLY — the gate closed underneath the write. A 422 (bad payload) must
  // fall through as a plain validation error so it is never misreported to the
  // supplier as "someone already started this item"; the composable then shows a
  // generic retry toast (`common.errorTryAgain`) rather than the frozen path.
  if (status === 409) {
    normalized.code = SUPPLIER_WRITE_FROZEN;
    normalized.frozenReason = extractFrozenReason(error); // null when the BE omits it
  }
  return normalized;
}

/**
 * Pull the updated line item out of a write response. jsonApiClient runs jsona
 * deserialization, so `response.data.data` is a flat object (id + attributes
 * spread) ready to `repo.save()`. Tolerant of an empty/204 body (e.g. a clear
 * that responds no-content) — returns null so the caller keeps its optimistic
 * value rather than blanking the row.
 */
function extractItem(response) {
  return response?.data?.data ?? null;
}

// The flat write path only needs the plan-item id. `boxId` is still carried on
// the item for grouping / day-label / poll-arming (useSupplierDailyOrders), but
// it is NOT part of the request URL any more, so the write no longer requires it.
function requireItemId(item, action) {
  const itemId = item?.id;
  if (itemId == null) {
    throw new Error(`[supplierDailyOrders.service] ${action} needs item.id (got id=${itemId})`);
  }
  return itemId;
}

/**
 * Reserve an item («معايا»). v1 ALWAYS reserves the full remaining quantity
 * (FE plan D-Δ3 — no partial UI), i.e. body `{ reservedQty: item.qty }`. The BE
 * returns `signal = RESERVED` (or `PARTIALLY_RESERVED` if a future partial write
 * sends less), which the composable saves as server truth.
 *
 * @param {{ id: string|number, qty: number }} item
 * @returns {Promise<object|null>} the updated item resource, or null on empty body
 * @throws Error with `code === SUPPLIER_WRITE_FROZEN` on a 409 gate rejection
 */
export async function reserveItem(item) {
  const itemId = requireItemId(item, 'reserveItem');
  try {
    const response = await jsonApiClient.post(RESERVE_PATH(itemId), {
      reservedQty: item.qty,
    });
    return extractItem(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

/**
 * Mark an item out of stock («نواقص»). Notifies the leader (BE-batched); the FE
 * just POSTs, no client batching.
 *
 * @param {{ id: string|number }} item
 * @returns {Promise<object|null>} the updated item resource, or null on empty body
 * @throws Error with `code === SUPPLIER_WRITE_FROZEN` on a 409 gate rejection
 */
export async function markOos(item) {
  const itemId = requireItemId(item, 'markOos');
  try {
    const response = await jsonApiClient.post(OOS_PATH(itemId), {});
    return extractItem(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

/**
 * Clear an item's signal back to unanswered (un-reserve / un-OOS).
 *
 * @param {{ id: string|number }} item
 * @returns {Promise<object|null>} the updated item resource, or null on 204/empty
 * @throws Error with `code === SUPPLIER_WRITE_FROZEN` on a 409 gate rejection
 */
export async function clearSignal(item) {
  const itemId = requireItemId(item, 'clearSignal');
  try {
    const response = await jsonApiClient.delete(SIGNAL_PATH(itemId));
    return extractItem(response);
  } catch (error) {
    throw normalizeError(error);
  }
}
